import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoJsonLd from '../../components/feature/SeoJsonLd';

const VALUES = [
  {
    icon: 'ri-heart-line',
    title: 'Commerce local d\'abord',
    desc: 'Nous croyons que les commerces de proximité sont le cœur de nos villes. Chaque commande passée sur Localygo soutient directement un commerçant de votre quartier.',
  },
  {
    icon: 'ri-community-line',
    title: 'Lien social & communauté',
    desc: 'Nos livreurs sont vos voisins. En choisissant Localygo, vous participez à créer du lien social et à renforcer l\'économie de votre territoire.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Impact environnemental réduit',
    desc: 'Des livraisons courtes, des livreurs locaux, moins de kilomètres parcourus. Notre modèle réduit l\'empreinte carbone par rapport aux grandes plateformes nationales.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Transparence & confiance',
    desc: 'Commissions justes pour les commerçants, rémunération équitable pour les livreurs, prix honnêtes pour les clients. Pas de surprise, pas de frais cachés.',
  },
];

const TEAM = [
  {
    name: 'Sophie Marchand',
    role: 'Co-fondatrice & CEO',
    bio: 'Ancienne directrice d\'une épicerie de quartier, Sophie a créé Localygo pour donner aux commerçants locaux les outils des grandes plateformes.',
    image: 'https://public.readdy.ai/ai/img_res/a4ef4d44c0d86d501dadf14d74414cf7.jpg',
  },
  {
    name: 'Karim Diallo',
    role: 'Co-fondateur & CTO',
    bio: 'Ingénieur passionné par la tech au service du local, Karim a développé la plateforme Localygo de A à Z avec une équipe de 5 développeurs.',
    image: 'https://readdy.ai/api/search-image?query=professional%20african%20french%20man%20tech%20engineer%20CTO%20portrait%20confident%20smile%20modern%20office%20background%20warm%20lighting%20business%20casual%2030s%20clean%20simple%20background%20professional%20headshot%20photography&width=400&height=400&seq=team002&orientation=squarish',
  },
  {
    name: 'Léa Fontaine',
    role: 'Directrice des Opérations',
    bio: 'Spécialiste de la logistique urbaine, Léa coordonne les partenariats avec les commerçants et optimise les circuits de livraison.',
    image: 'https://readdy.ai/api/search-image?query=professional%20french%20woman%20operations%20director%20portrait%20confident%20smile%20modern%20office%20background%20warm%20lighting%20business%20attire%2028s%20clean%20simple%20background%20professional%20headshot%20photography&width=400&height=400&seq=team003&orientation=squarish',
  },
  {
    name: 'Thomas Renard',
    role: 'Responsable Communauté',
    bio: 'Thomas anime la communauté des livreurs et commerçants Localygo. Il organise les événements locaux et veille à la satisfaction de tous.',
    image: 'https://readdy.ai/api/search-image?query=professional%20french%20man%20community%20manager%20portrait%20friendly%20smile%20modern%20office%20background%20warm%20lighting%20casual%20business%2032s%20clean%20simple%20background%20professional%20headshot%20photography&width=400&height=400&seq=team004&orientation=squarish',
  },
];

const MILESTONES = [
  { year: '2022', title: 'Naissance de l\'idée', desc: 'Sophie et Karim se rencontrent lors d\'un hackathon dédié à l\'économie locale. L\'idée de Localygo est née.' },
  { year: '2023', title: 'Lancement en beta', desc: 'Première version de la plateforme lancée dans 2 villes pilotes avec 15 commerçants partenaires.' },
  { year: '2024', title: 'Expansion régionale', desc: 'Localygo s\'étend à 12 villes, dépasse les 200 livreurs actifs et 50 commerces partenaires.' },
  { year: '2025', title: 'Aujourd\'hui', desc: 'Plus de 10 000 commandes livrées, une note moyenne de 4.8/5 et une communauté grandissante.' },
];

const STATS = [
  { value: '50+', label: 'Commerces partenaires' },
  { value: '200+', label: 'Livreurs actifs' },
  { value: '10 000+', label: 'Commandes livrées' },
  { value: '12', label: 'Villes couvertes' },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const aboutPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'À propos de Localygo – Livraison locale & commerce de proximité',
  url: `${SITE_URL}/a-propos`,
  description: 'Découvrez l\'histoire, la mission et l\'équipe de Localygo, la plateforme de livraison locale qui reconnecte habitants et commerçants de proximité.',
  publisher: {
    '@type': 'Organization',
    name: 'Localygo',
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: 'https://public.readdy.ai/ai/img_res/b05e588d-5161-4e8a-9fc5-c87d2449f69a.png',
    },
  },
};

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Localygo',
  url: SITE_URL,
  logo: 'https://public.readdy.ai/ai/img_res/b05e588d-5161-4e8a-9fc5-c87d2449f69a.png',
  description: 'Localygo est une plateforme de livraison locale connectant restaurants, livreurs indépendants et clients dans leur quartier.',
  foundingDate: '2022',
  numberOfEmployees: { '@type': 'QuantitativeValue', value: 10 },
  areaServed: { '@type': 'Country', name: 'France' },
  sameAs: [],
};

export default function AProposPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setContactForm({ ...contactForm, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = new URLSearchParams();
      Object.entries(contactForm).forEach(([k, v]) => body.append(k, v));
      await fetch('https://readdy.ai/api/form/d6n45dp4a3uojp5fh0d0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoJsonLd data={aboutPageSchema} id="schema-apropos" />
      <SeoJsonLd data={organizationSchema} id="schema-org-apropos" />
      <Navbar />

      {/* Hero */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=vibrant%20french%20local%20neighborhood%20community%20market%20scene%20people%20shopping%20small%20businesses%20colorful%20storefronts%20warm%20sunny%20day%20community%20spirit%20local%20economy%20thriving%20street%20life%20wide%20angle%20photography&width=1440&height=700&seq=apropos-hero&orientation=landscape"
          alt="À propos de Localygo"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        <div className="relative z-10 text-center px-4 w-full">
          <span className="inline-block bg-teal-500/20 border border-teal-400/40 text-teal-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-widest">
            Notre histoire
          </span>
          <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-5">
            À propos de <span className="text-teal-400">Localygo</span>
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Une plateforme née d'une conviction : le commerce local mérite les mêmes outils que les grandes enseignes.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-teal-600 font-semibold text-sm uppercase tracking-widest mb-3 block">Notre mission</span>
              <h2 className="text-4xl font-extrabold text-gray-900 mb-6 leading-tight">
                Reconnecter les habitants avec leurs commerçants locaux
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-5">
                Localygo est né d'un constat simple : les grandes plateformes de livraison favorisent les chaînes nationales au détriment des petits commerçants. Nous avons voulu inverser cette tendance.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Notre plateforme connecte directement les habitants avec les restaurants, épiceries, boulangeries et boutiques de leur quartier. Les livraisons sont assurées par des livreurs locaux indépendants, créant ainsi un véritable écosystème de proximité.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/restaurants"
                  className="bg-teal-600 text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Découvrir les commerces
                </Link>
                <Link
                  to="/livraison"
                  className="border border-teal-600 text-teal-600 px-6 py-3 rounded-full font-semibold text-sm hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Devenir livreur
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=local%20french%20bakery%20owner%20smiling%20proudly%20in%20front%20of%20shop%20fresh%20bread%20display%20warm%20welcoming%20atmosphere%20small%20business%20owner%20portrait%20community%20connection%20artisan%20baker%20clean%20simple%20background&width=700&height=600&seq=apropos-mission&orientation=portrait"
                  alt="Commerçant local Localygo"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-teal-600 text-white rounded-xl p-5 shadow-lg">
                <div className="text-3xl font-extrabold">4.8★</div>
                <div className="text-xs text-teal-100 mt-1">Note moyenne clients</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {STATS.map(stat => (
              <div key={stat.label} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="text-4xl font-extrabold text-teal-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Valeurs */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Nos valeurs</h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">Ce qui nous guide chaque jour dans le développement de Localygo.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {VALUES.map(v => (
              <div key={v.title} className="flex gap-5 p-8 bg-gray-50 rounded-2xl hover:shadow-sm transition-all">
                <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-xl shrink-0">
                  <i className={`${v.icon} text-2xl text-teal-600`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-base mb-2">{v.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Notre parcours</h2>
            <p className="text-gray-500 text-lg">De l'idée à la réalité, en quelques étapes clés.</p>
          </div>
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-teal-200"></div>
            <div className="space-y-10">
              {MILESTONES.map((m, i) => (
                <div key={i} className="flex gap-8 items-start">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white rounded-full shrink-0 font-extrabold text-sm shadow-lg z-10">
                    {m.year}
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm flex-1 mt-2">
                    <h3 className="font-bold text-gray-900 text-base mb-2">{m.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Équipe */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">L'équipe Localygo</h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">Des passionnés du commerce local qui travaillent chaque jour pour vous.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {TEAM.map(member => (
              <div key={member.name} className="text-center group">
                <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-4 shadow-md group-hover:shadow-lg transition-all">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <h3 className="font-bold text-gray-900 text-base mb-0.5">{member.name}</h3>
                <p className="text-teal-600 text-xs font-semibold mb-3">{member.role}</p>
                <p className="text-gray-500 text-xs leading-relaxed">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Contactez-nous</h2>
            <p className="text-gray-500 text-base">Une question, une suggestion ou un partenariat ? Écrivez-nous.</p>
          </div>

          {submitted ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-6">
                <i className="ri-check-line text-4xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Message envoyé !</h3>
              <p className="text-gray-500 text-base mb-6">Merci pour votre message. Nous vous répondrons dans les 24h.</p>
              <button
                onClick={() => { setSubmitted(false); setContactForm({ name: '', email: '', subject: '', message: '' }); }}
                className="bg-teal-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Envoyer un autre message
              </button>
            </div>
          ) : (
            <form
              data-readdy-form
              id="contact-localygo"
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl shadow-lg p-10 space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nom complet <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={contactForm.name}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleChange}
                    placeholder="votre@email.fr"
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Sujet <span className="text-red-500">*</span></label>
                <select
                  name="subject"
                  value={contactForm.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                >
                  <option value="">Sélectionnez un sujet</option>
                  <option value="Partenariat commerçant">Partenariat commerçant</option>
                  <option value="Devenir livreur">Devenir livreur</option>
                  <option value="Support client">Support client</option>
                  <option value="Presse & Médias">Presse &amp; Médias</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message <span className="text-red-500">*</span></label>
                <textarea
                  name="message"
                  value={contactForm.message}
                  onChange={handleChange}
                  placeholder="Votre message..."
                  rows={5}
                  required
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                />
                <div className="text-right text-xs text-gray-400 mt-1">{contactForm.message.length}/500</div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-4 rounded-full font-semibold text-base transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i> Envoi...
                  </span>
                ) : 'Envoyer le message'}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
